// POI search for the Neighborhood module. The commute address-autocomplete
// half (getPlacePredictions/getPlaceDetails) lives in
// src/components/features/commute/googlePlaces.ts — it's shared with the SRP
// commute filter, not HDP-specific.
import type { POIOptionValue, POIPlace } from './types';
import { getPOISearchConfig } from './types';

const POI_SEARCH_RADIUS_METERS = 800;
const POI_RESULT_LIMIT = 15;
const POI_DETAIL_FIELDS = ['displayName', 'formattedAddress', 'location', 'rating', 'userRatingCount'];
const LEGACY_POI_DETAIL_FIELDS = [
  'formatted_address',
  'geometry.location',
  'html_attributions',
  'name',
  'rating',
  'user_ratings_total',
  'vicinity',
];

export async function searchPOI(
  map: google.maps.Map,
  centerLat: number,
  centerLng: number,
  optionValue: POIOptionValue,
): Promise<Array<POIPlace>> {
  if (typeof google === 'undefined' || !google.maps?.places) return [];

  const config = getPOISearchConfig(optionValue);

  if (google.maps.places.Place?.searchNearby) {
    try {
      const { places } = await google.maps.places.Place.searchNearby({
        fields: ['id', 'displayName', 'location', 'rating', 'userRatingCount'],
        includedTypes: [...config.placesTypes],
        locationRestriction: {
          center: { lat: centerLat, lng: centerLng },
          radius: POI_SEARCH_RADIUS_METERS,
        },
        maxResultCount: POI_RESULT_LIMIT,
      });

      return places
        .map((place): POIPlace | null => {
          const location = place.location;
          if (!location) return null;

          const rating = place.rating ?? undefined;
          const userRatingCount = place.userRatingCount ?? undefined;
          return {
            placeId: place.id,
            name: place.displayName ?? '',
            lat: location.lat(),
            lng: location.lng(),
            category: optionValue,
            ...(rating === undefined ? {} : { rating }),
            ...(userRatingCount === undefined ? {} : { userRatingCount }),
          };
        })
        .filter((place): place is POIPlace => place !== null);
    } catch {
      // Fall through to the legacy PlacesService search when Places (New) is unavailable.
    }
  }

  const service = new google.maps.places.PlacesService(map);

  const resultSets = await Promise.all(
    config.placesTypes.map(
      (type) =>
        new Promise<Array<POIPlace>>((resolve) => {
          service.nearbySearch(
            {
              location: { lat: centerLat, lng: centerLng },
              radius: POI_SEARCH_RADIUS_METERS,
              type,
            },
            (results, status) => {
              if (status !== google.maps.places.PlacesServiceStatus.OK || !results) {
                resolve([]);
                return;
              }

              resolve(
                results.map((place) => {
                  const userRatingCount = place.user_ratings_total ?? undefined;
                  return {
                    placeId: place.place_id ?? '',
                    name: place.name ?? '',
                    lat: place.geometry?.location?.lat() ?? 0,
                    lng: place.geometry?.location?.lng() ?? 0,
                    category: optionValue,
                    address: place.vicinity,
                    rating: place.rating,
                    ...(userRatingCount === undefined ? {} : { userRatingCount }),
                    attributionHtml: place.html_attributions,
                  };
                }),
              );
            },
          );
        }),
    ),
  );

  const dedupedPlaces = new Map<string, POIPlace>();
  for (const place of resultSets.flat()) {
    const key = place.placeId || `${place.name}-${place.lat}-${place.lng}`;
    if (!dedupedPlaces.has(key)) {
      dedupedPlaces.set(key, place);
    }
  }

  return Array.from(dedupedPlaces.values()).slice(0, POI_RESULT_LIMIT);
}

function mergePlaceDetails(place: POIPlace, details: google.maps.places.Place): POIPlace {
  const location = details.location;
  const rating = details.rating ?? place.rating;
  const userRatingCount = details.userRatingCount ?? place.userRatingCount;

  return {
    ...place,
    name: details.displayName ?? place.name,
    address: details.formattedAddress ?? place.address,
    lat: location?.lat() ?? place.lat,
    lng: location?.lng() ?? place.lng,
    ...(rating === undefined || rating === null ? {} : { rating }),
    ...(userRatingCount === undefined || userRatingCount === null ? {} : { userRatingCount }),
  };
}

async function getPOIDetailsFromPlaceNew(place: POIPlace): Promise<POIPlace> {
  const details = new google.maps.places.Place({ id: place.placeId });
  const { place: fetchedPlace } = await details.fetchFields({ fields: POI_DETAIL_FIELDS });
  return mergePlaceDetails(place, fetchedPlace);
}

async function getLegacyPOIDetails(map: google.maps.Map, place: POIPlace): Promise<POIPlace> {
  const service = new google.maps.places.PlacesService(map);

  return new Promise((resolve) => {
    service.getDetails(
      {
        placeId: place.placeId,
        fields: LEGACY_POI_DETAIL_FIELDS,
      },
      (details, status) => {
        if (status !== google.maps.places.PlacesServiceStatus.OK || !details) {
          resolve(place);
          return;
        }

        const location = details.geometry?.location;
        const rating = details.rating ?? place.rating;
        const userRatingCount = details.user_ratings_total ?? place.userRatingCount;

        resolve({
          ...place,
          name: details.name ?? place.name,
          address: details.formatted_address ?? details.vicinity ?? place.address,
          lat: location?.lat() ?? place.lat,
          lng: location?.lng() ?? place.lng,
          ...(rating === undefined || rating === null ? {} : { rating }),
          ...(userRatingCount === undefined || userRatingCount === null ? {} : { userRatingCount }),
          attributionHtml: details.html_attributions ?? place.attributionHtml,
        });
      },
    );
  });
}

export async function getPOIDetails(map: google.maps.Map, place: POIPlace): Promise<POIPlace> {
  if (typeof google === 'undefined' || !google.maps?.places || !place.placeId) {
    return place;
  }

  const PlaceConstructor = google.maps.places.Place as
    | (typeof google.maps.places.Place & {
        prototype?: { fetchFields?: unknown };
      })
    | undefined;

  if (typeof PlaceConstructor === 'function' && typeof PlaceConstructor.prototype?.fetchFields === 'function') {
    try {
      return await getPOIDetailsFromPlaceNew(place);
    } catch {
      // Fall through to the legacy details API when Places (New) rejects.
    }
  }

  return getLegacyPOIDetails(map, place);
}
